/* @jsx h */
import { defineComponent, h, ref, PropType } from "vue";
import { RightSheet, ResponsiveTable } from "@lemmonhq/uikit";
import type { ResponsiveTableColumn } from "@lemmonhq/uikit";
import type { TaxItemType } from "@lemmonhq/types";
import { useToast } from "@app/vue/providers/Toast";
import {
  taxSettingsAPI,
  type CreateTaxItemTypeData,
  type UpdateTaxItemTypeData,
} from "@app/data/api/tax_settings";
import { formatDateTime, defaultItemTypeForm, type ItemTypeFormState } from "./utils";

export const ItemTypesTab = defineComponent({
  name: "ItemTypesTab",
  props: {
    itemTypes: { type: Array as PropType<TaxItemType[]>, required: true },
    onRefresh: {
      type: Function as PropType<() => Promise<void>>,
      required: true,
    },
  },
  setup(props) {
    const toast = useToast();
    const isSheetOpen = ref(false);
    const selectedItemType = ref<TaxItemType | null>(null);
    const mode = ref<"create" | "edit" | "view">("create");
    const form = ref<ItemTypeFormState>(defaultItemTypeForm());
    const submitting = ref(false);

    const openCreate = () => {
      mode.value = "create";
      selectedItemType.value = null;
      form.value = defaultItemTypeForm();
      isSheetOpen.value = true;
    };

    const openSheet = (itemType: TaxItemType) => {
      selectedItemType.value = itemType;
      if (!itemType.is_editable) {
        mode.value = "view";
      } else {
        mode.value = "edit";
        form.value = {
          name: itemType.name,
          description: itemType.description ?? "",
        };
      }
      isSheetOpen.value = true;
    };

    const closeSheet = () => {
      isSheetOpen.value = false;
      selectedItemType.value = null;
    };

    const handleCreate = async (event: Event) => {
      event.preventDefault();
      if (submitting.value) return;

      if (!form.value.name.trim()) {
        toast.showToast("Enter a name for the item type.", { type: "warning" });
        return;
      }

      const payload: CreateTaxItemTypeData = {
        name: form.value.name.trim(),
        description: form.value.description.trim() || undefined,
      };

      try {
        submitting.value = true;
        await taxSettingsAPI.createTaxItemType(payload);
        await props.onRefresh();
        toast.showToast("Item type created.", { type: "success" });
        closeSheet();
      } catch (error) {
        console.error("Failed to create tax item type", error);
        toast.showToast(
          "We could not create that item type. Please try again.",
          { type: "error" },
        );
      } finally {
        submitting.value = false;
      }
    };

    const handleUpdate = async (event: Event) => {
      event.preventDefault();
      if (!selectedItemType.value || submitting.value) return;

      const payload: UpdateTaxItemTypeData = {
        name: form.value.name.trim() || undefined,
        description: form.value.description.trim() || null,
      };

      try {
        submitting.value = true;
        await taxSettingsAPI.updateTaxItemType(selectedItemType.value.id, payload);
        await props.onRefresh();
        toast.showToast("Item type updated.", { type: "success" });
        closeSheet();
      } catch (error) {
        console.error("Failed to update tax item type", error);
        toast.showToast("Updating that item type failed. Please try again.", {
          type: "error",
        });
      } finally {
        submitting.value = false;
      }
    };

    const handleDelete = async () => {
      if (!selectedItemType.value || submitting.value) return;
      try {
        submitting.value = true;
        await taxSettingsAPI.deleteTaxItemType(selectedItemType.value.id);
        await props.onRefresh();
        toast.showToast("Item type deleted.", { type: "success" });
        closeSheet();
      } catch (error) {
        console.error("Failed to delete tax item type", error);
        toast.showToast("Deleting that item type failed. Please try again.", {
          type: "error",
        });
      } finally {
        submitting.value = false;
      }
    };

    const columns: ResponsiveTableColumn[] = [
      {
        key: "d-name",
        label: "Name",
        icon: "category",
        mobilePriority: "hidden",
        render: (itemType: TaxItemType) => (
          <div class="font-medium text-base-content">{itemType.name}</div>
        ),
      },
      {
        key: "d-desc",
        label: "Description",
        mobilePriority: "hidden",
        render: (itemType: TaxItemType) => (
          <span class="text-sm text-base-content/70">
            {itemType.description || "—"}
          </span>
        ),
      },
      {
        key: "d-editable",
        label: "Type",
        mobilePriority: "hidden",
        render: (itemType: TaxItemType) => (
          <span
            class={`badge badge-sm ${itemType.is_editable ? "badge-success" : "badge-neutral"}`}
          >
            {itemType.is_editable ? "Editable" : "Locked"}
          </span>
        ),
      },
      {
        key: "d-created",
        label: "Created",
        icon: "event",
        mobilePriority: "hidden",
        render: (itemType: TaxItemType) => (
          <span class="text-sm text-base-content/70">
            {formatDateTime(itemType.created_at)}
          </span>
        ),
      },
      {
        key: "m-name",
        label: "Name",
        desktopHide: true,
        mobilePriority: "primary",
        render: (itemType: TaxItemType) => (
          <div class="min-w-0">
            <div class="truncate text-sm font-medium text-base-content">
              {itemType.name}
            </div>
            {itemType.description && (
              <div class="truncate text-xs text-base-content/60">
                {itemType.description}
              </div>
            )}
          </div>
        ),
      },
      {
        key: "m-editable",
        label: "Type",
        desktopHide: true,
        mobilePriority: "primary",
        mobilePrimaryClass: "flex items-center shrink-0",
        render: (itemType: TaxItemType) => (
          <span
            class={`badge badge-sm ${itemType.is_editable ? "badge-success" : "badge-neutral"}`}
          >
            {itemType.is_editable ? "Editable" : "Locked"}
          </span>
        ),
      },
      {
        key: "m-created",
        label: "Created",
        desktopHide: true,
        mobilePriority: "secondary",
        render: (itemType: TaxItemType) => formatDateTime(itemType.created_at),
      },
      {
        key: "actions",
        label: "",
        mobilePriority: "hidden",
        headerClass: "w-px",
        cellClass: "text-right whitespace-nowrap",
        render: (itemType: TaxItemType) => (
          <button
            class="btn btn-sm btn-secondary"
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              openSheet(itemType);
            }}
          >
            <span class="material-symbols-outlined text-2xl">table_edit</span>
          </button>
        ),
      },
    ];

    const itemTypeFormFields = () => (
      <div class="space-y-4">
        <label class="form-control w-full">
          <span class="label-text text-sm font-medium text-base-content/70">
            Item type name
          </span>
          <input
            class="input input-bordered"
            placeholder="Bottled beverage"
            value={form.value.name}
            onInput={(e) =>
              (form.value.name = (e.target as HTMLInputElement).value)
            }
          />
        </label>
        <label class="form-control w-full">
          <span class="label-text text-sm font-medium text-base-content/70">
            Description (optional)
          </span>
          <textarea
            class="textarea textarea-bordered min-h-[120px]"
            placeholder="Describe when to use this item type."
            value={form.value.description}
            onInput={(e) =>
              (form.value.description = (
                e.target as HTMLTextAreaElement
              ).value)
            }
          />
        </label>
      </div>
    );

    return () => (
      <>
        <div class="flex items-center justify-end px-4 py-3 border-b border-base-content/5">
          <button class="btn btn-primary btn-sm gap-2" onClick={openCreate}>
            <span class="material-symbols-outlined text-base">add</span>
            New item type
          </button>
        </div>

        {props.itemTypes.length === 0 ? (
          <div class="flex min-h-[280px] flex-col items-center justify-center px-8 py-12 text-center">
            <div class="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-base-200">
              <span class="material-symbols-outlined text-2xl text-base-content/40">
                category
              </span>
            </div>
            <p class="font-medium text-base-content">
              No custom item types yet
            </p>
            <p class="mt-1 text-sm text-base-content/60">
              Create item types to target profiles more precisely than the
              default catalogue.
            </p>
          </div>
        ) : (
          <ResponsiveTable
            columns={columns}
            rows={props.itemTypes}
            rowKey={(itemType: TaxItemType) => itemType.id}
            onRowClick={openSheet}
            emptyMessage="No item types found"
          />
        )}

        <RightSheet
          isOpen={isSheetOpen.value}
          title={
            mode.value === "create"
              ? "New item type"
              : mode.value === "edit"
                ? "Edit item type"
                : selectedItemType.value?.name ?? "Item type"
          }
          onClose={closeSheet}
          width="520px"
        >
          {mode.value === "create" && (
            <form class="flex h-full flex-col" onSubmit={handleCreate}>
              <div class="flex-1 space-y-6 overflow-y-auto px-6 py-6">
                <p class="text-sm text-base-content/70">
                  Item types allow tax profiles to target specific product
                  groups, such as bottled drinks or packaged goods.
                </p>
                {itemTypeFormFields()}
              </div>
              <div class="border-t border-base-200 bg-base-200/50 px-6 py-4 flex items-center justify-between">
                <button
                  type="button"
                  class="btn btn-ghost btn-sm"
                  onClick={closeSheet}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  class="btn btn-primary btn-sm"
                  disabled={submitting.value}
                >
                  {submitting.value ? "Saving…" : "Save item type"}
                </button>
              </div>
            </form>
          )}

          {mode.value === "edit" && (
            <form class="flex h-full flex-col" onSubmit={handleUpdate}>
              <div class="flex-1 space-y-6 overflow-y-auto px-6 py-6">
                <p class="text-sm text-base-content/70">
                  Update the name or description of this item type. Existing
                  profiles referencing it update instantly.
                </p>
                {itemTypeFormFields()}
              </div>
              <div class="border-t border-base-200 bg-base-200/50 px-6 py-4 flex flex-wrap items-center justify-between gap-3">
                <button
                  type="button"
                  class="btn btn-ghost btn-sm text-error"
                  onClick={handleDelete}
                  disabled={submitting.value}
                >
                  Delete item type
                </button>
                <div class="flex gap-2">
                  <button
                    type="button"
                    class="btn btn-ghost btn-sm"
                    onClick={closeSheet}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    class="btn btn-primary btn-sm"
                    disabled={submitting.value}
                  >
                    {submitting.value ? "Saving…" : "Save changes"}
                  </button>
                </div>
              </div>
            </form>
          )}

          {mode.value === "view" && selectedItemType.value && (
            <div class="flex h-full flex-col">
              <div class="flex-1 space-y-4 overflow-y-auto px-6 py-6">
                <p class="text-sm text-base-content/70">
                  This item type is part of Lemmon's default compliance
                  catalogue and cannot be edited. Create your own variant to
                  customise it.
                </p>
                <section class="rounded-2xl border  bg-base-100 p-5 shadow-sm space-y-3">
                  <div>
                    <h3 class="text-xs uppercase tracking-wide text-base-content/60">
                      Description
                    </h3>
                    <p class="text-base text-base-content">
                      {selectedItemType.value.description ||
                        "No description provided."}
                    </p>
                  </div>
                  <div>
                    <h3 class="text-xs uppercase tracking-wide text-base-content/60">
                      Created
                    </h3>
                    <p class="text-base text-base-content">
                      {formatDateTime(selectedItemType.value.created_at)}
                    </p>
                  </div>
                </section>
              </div>
              <div class="border-t border-base-200 bg-base-200/50 px-6 py-4 flex items-center justify-end">
                <button
                  type="button"
                  class="btn btn-ghost btn-sm"
                  onClick={closeSheet}
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </RightSheet>
      </>
    );
  },
});
